import React, { useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDragScroll } from '../../../hooks/useDragScroll';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FormattedMessage,
  injectIntl,
  intlShape,
  useIntl,
} from '../../../util/reactIntl';
import { ExternalLink, NamedLink } from '../../../components';
import css from './StorefrontTabContent.module.css';
import { propTypes } from '../../../util/types';
import IntroText from '../IntroText/IntroText';
import { isHTMLString } from '../../../util/data';
import GalleryView from '../../MediaLibraryPage/GalleryView/GalleryView';
import ReviewsTab from '../ReviewsTab/ReviewsTab';
import FAQ from '../../../components/FAQ/FAQ';
import {
  DAYS_OF_WEEK,
  getStoreHoursStatus,
  getStoreTimeZone,
} from '../../StoreHoursPage/StoreHoursForm/storeHoursUtils';
import OwnerAboutEmptyWelcomeVideoCard from './OwnerAboutEmptyWelcomeVideoCard/OwnerAboutEmptyWelcomeVideoCard';
import OwnerAboutEmptyStoreHoursCard from './OwnerAboutEmptyStoreHoursCard/OwnerAboutEmptyStoreHoursCard';
import OwnerAboutEmptyIntroductionCard from './OwnerAboutEmptyIntroductionCard/OwnerAboutEmptyIntroductionCard';
import OwnerFaqsTabEmptyState from './OwnerFaqsTabEmptyState/OwnerFaqsTabEmptyState';
import OwnerGalleryTabEmptyState from './OwnerGalleryTabEmptyState/OwnerGalleryTabEmptyState';
import OwnerBlogTabEmptyState from './OwnerBlogTabEmptyState/OwnerBlogTabEmptyState';
import BlogListSkeleton from '../../../components/BlogListSkeleton/BlogListSkeleton';
import { getStoreBlogId, getStoreBlogLinkTarget } from '../../../util/storeBlogs';

const USE_PUBLICDATA_REVIEW_SUMMARY_UI =
  process.env.REACT_APP_USE_PUBLICDATA_REVIEW_SUMMARY_UI !== 'false';

const isNonEmptyString = v => typeof v === 'string' && v.trim().length > 0;
const hasStoreHoursData = hours =>
  hours && typeof hours === 'object' && Object.keys(hours).length > 0;

const isNonEmptyArray = v => Array.isArray(v) && v.length > 0;

/** Localized 12h time from `HH:mm` (store hours storage format). */
const formatTime24WithIntl = (intl, time24) => {
  if (!time24) return '';
  const [hRaw, mRaw] = time24.split(':');
  const h = parseInt(hRaw, 10);
  const m = parseInt(mRaw, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return time24;
  const d = new Date(2000, 0, 1, h, m, 0, 0);
  return intl.formatTime(d, { hour: 'numeric', minute: '2-digit', hour12: true });
};

/**
 * Generates a Cloudinary srcset string for responsive images.
 * Inserts width transforms after "/upload/" in the Cloudinary URL.
 */
const buildCloudinarySrcSet = url => {
  if (!url || !url.includes('/upload/')) return undefined;
  return [400, 800, 1200]
    .map(w => `${url.replace('/upload/', `/upload/w_${w},f_auto,q_auto/`)} ${w}w`)
    .join(', ');
};

/**
 * Derives a Cloudinary video poster (JPEG thumbnail) from the video upload URL.
 * Uses the first second of the video as the thumbnail frame.
 */
const buildCloudinaryVideoPoster = url => {
  if (!url || !url.includes('/video/upload/')) return undefined;
  return url
    .replace('/video/upload/', '/video/upload/w_800,so_1,f_jpg/')
    .replace(/\.[^.]+$/, '.jpg');
};

/** Single sortable gallery item for owner reorder (filter "all" only) */
const SortableGalleryItem = ({ media, onOpenGallery, showDragHandle, alt }) => {
  const intl = useIntl();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: media.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames(css.galleryItem, css.galleryItemSortable, {
        [css.galleryItemDragging]: isDragging,
      })}
    >
      <button
        type="button"
        className={css.galleryItemButton}
        onClick={() => onOpenGallery(media.id)}
      >
        {media.type === 'video' ? (
          <>
            <video
              src={media.secure_url || media.playback_url}
              className={css.galleryImg}
              muted
              preload="metadata"
              poster={buildCloudinaryVideoPoster(media.secure_url || media.playback_url)}
              aria-label={alt}
              title={media.seoTags?.titleTag || ''}
            />
            <div className={css.videoPlayOverlay} aria-hidden>
              <span className={css.videoPlayIcon}>
                <i className="ri-play-fill" />
              </span>
            </div>
          </>
        ) : (
          <img
            className={css.galleryImg}
            src={media.secure_url}
            srcSet={buildCloudinarySrcSet(media.secure_url)}
            sizes="(max-width: 767px) calc(50vw - 16px), (max-width: 1199px) calc(33vw - 16px), 400px"
            alt={alt}
            title={media.seoTags?.titleTag || ''}
            width={media.width  || undefined}
            height={media.height || undefined}
            loading="lazy"
            decoding="async"
          />
        )}
      </button>
      {showDragHandle && (
        <div
          {...attributes}
          {...listeners}
          className={css.galleryDragHandle}
          onClick={e => e.stopPropagation()}
          title={intl.formatMessage({
            id: 'StorefrontTabContent.gallery.dragToReorder',
            defaultMessage: 'Drag to reorder',
          })}
          aria-label={intl.formatMessage({
            id: 'StorefrontTabContent.gallery.dragToReorder',
            defaultMessage: 'Drag to reorder',
          })}
        >
          <i className="ri-draggable" aria-hidden />
        </div>
      )}
    </div>
  );
};

SortableGalleryItem.propTypes = {
  media: PropTypes.shape({
    id: PropTypes.string,
    public_id: PropTypes.string,
    secure_url: PropTypes.string,
    type: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    seoTags: PropTypes.shape({
      altText: PropTypes.string,
      titleTag: PropTypes.string,
      caption: PropTypes.string,
      fileName: PropTypes.string,
    }),
  }).isRequired,
  onOpenGallery: PropTypes.func.isRequired,
  showDragHandle: PropTypes.bool,
  alt: PropTypes.string,
};

const renderFaqs = (storeFaqs, isCurrentUser) => {
  if (!isNonEmptyArray(storeFaqs)) {
    if (!isCurrentUser) return null;
    return (
      <div className={css.section}>
        <OwnerFaqsTabEmptyState />
      </div>
    );
  }

  return (
    <div className={css.section}>
      <section className={css.faqContent}>
        {storeFaqs?.length > 0 && <FAQ faq={storeFaqs} />}
      </section>
    </div>
  );
};

const renderGallery = (storeMedia, isCurrentUser, onReorderStoreMedia, intl, storeName) => {
  // Gallery modal state
  const [galleryViewIndex, setGalleryViewIndex] = useState(null);

  // Filter state - persist in sessionStorage
  const getInitialFilter = () => {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const stored = window.sessionStorage.getItem('storeGalleryFilter');
      if (stored === 'all' || stored === 'photos' || stored === 'videos') {
        return stored;
      }
    }
    return 'all';
  };

  const [activeFilter, setActiveFilter] = useState(getInitialFilter);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 10 },
    })
  );

  // Persist filter state
  const handleFilterChange = useCallback(filter => {
    setActiveFilter(filter);
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.setItem('storeGalleryFilter', filter);
    }
  }, []);

  // Open gallery view for specific media item
  const openGalleryView = useCallback(
    mediaId => {
      if (!storeMedia || !Array.isArray(storeMedia)) return;

      const index = storeMedia.findIndex(media => media.id === mediaId);
      if (index !== -1) {
        setGalleryViewIndex(index);
      }
    },
    [storeMedia]
  );

  // Close gallery view
  const closeGalleryView = useCallback(() => {
    setGalleryViewIndex(null);
  }, []);

  // Navigate gallery
  const navigateGallery = useCallback(
    direction => {
      if (
        !storeMedia ||
        !Array.isArray(storeMedia) ||
        galleryViewIndex === null
      )
        return;

      let newIndex;
      if (direction === 'next') {
        newIndex =
          galleryViewIndex < storeMedia.length - 1 ? galleryViewIndex + 1 : 0;
      } else if (direction === 'prev') {
        newIndex =
          galleryViewIndex > 0 ? galleryViewIndex - 1 : storeMedia.length - 1;
      }
      setGalleryViewIndex(newIndex);
    },
    [storeMedia, galleryViewIndex]
  );

  // Set specific gallery index
  const setGalleryIndex = useCallback(index => {
    setGalleryViewIndex(index);
  }, []);

  const handleDragEnd = useCallback(
    event => {
      const { active, over } = event;
      if (
        !over ||
        active.id === over.id ||
        !onReorderStoreMedia ||
        !storeMedia?.length
      )
        return;

      const oldIndex = storeMedia.findIndex(m => m.id === active.id);
      const newIndex = storeMedia.findIndex(m => m.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = [...storeMedia];
      const [removed] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, removed);
      onReorderStoreMedia(reordered);
    },
    [storeMedia, onReorderStoreMedia]
  );

  if (!isNonEmptyArray(storeMedia)) {
    if (!isCurrentUser) return null;
    return (
      <div className={css.section}>
        <OwnerGalleryTabEmptyState />
      </div>
    );
  }
  const images = storeMedia.filter(
    m => m?.type === 'photo' || m?.type === 'image'
  );
  const videos = storeMedia.filter(m => m?.type === 'video');

  // Filter media based on active filter
  const filteredMedia = storeMedia.filter(m => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'photos')
      return m?.type === 'photo' || m?.type === 'image';
    if (activeFilter === 'videos') return m?.type === 'video';
    return true;
  });

  // Pre-compute descriptive alt text for every media item, keyed by its stable id.
  // Counters are per-type so each photo and video gets an independent sequence number.
  const altMap = (() => {
    const label = storeName || 'Store';
    let imgIdx = 0;
    let vidIdx = 0;
    const map = new Map();
    storeMedia.forEach(m => {
      const key = m.id || m.secure_url;
      const fallback =
        m.type === 'video'
          ? `${label} gallery video ${++vidIdx}`
          : `${label} gallery photo ${++imgIdx}`;
      map.set(key, m.seoTags?.altText || fallback);
    });
    return map;
  })();

  const canReorder =
    isCurrentUser &&
    activeFilter === 'all' &&
    typeof onReorderStoreMedia === 'function';

  const galleryGridContent = canReorder ? (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={storeMedia.map(m => m.id)}
        strategy={rectSortingStrategy}
      >
        <div className={css.galleryGrid}>
          {storeMedia.map(m => (
            <SortableGalleryItem
              key={m.id || m.public_id || m.secure_url}
              media={m}
              alt={altMap.get(m.id || m.secure_url)}
              onOpenGallery={openGalleryView}
              showDragHandle
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  ) : (
    <div className={css.galleryGrid}>
      {filteredMedia.map(m => (
        <button
          key={m.id || m.public_id || m.secure_url}
          className={css.galleryItem}
          onClick={() => openGalleryView && openGalleryView(m.id)}
          type="button"
        >
          {m.type === 'video' ? (
            <>
              <video
                src={m.secure_url || m.playback_url}
                className={css.galleryImg}
                muted
                preload="metadata"
                poster={buildCloudinaryVideoPoster(m.secure_url || m.playback_url)}
                aria-label={altMap.get(m.id || m.secure_url)}
                title={m.seoTags?.titleTag || ''}
              />
              <div className={css.videoPlayOverlay} aria-hidden>
                <span className={css.videoPlayIcon}>
                  <i className="ri-play-fill" />
                </span>
              </div>
            </>
          ) : (
            <img
              className={css.galleryImg}
              src={m.secure_url}
              srcSet={buildCloudinarySrcSet(m.secure_url)}
              sizes="(max-width: 767px) calc(50vw - 16px), (max-width: 1199px) calc(33vw - 16px), 400px"
              alt={altMap.get(m.id || m.secure_url)}
              title={m.seoTags?.titleTag || ''}
              width={m.width  || undefined}
              height={m.height || undefined}
              loading="lazy"
              decoding="async"
            />
          )}
        </button>
      ))}
    </div>
  );

  return (
    <div className={css.section}>
      {/* Gallery View Modal */}
      {galleryViewIndex !== null && storeMedia && (
        <GalleryView
          media={storeMedia}
          currentIndex={galleryViewIndex}
          onClose={closeGalleryView}
          onNavigate={navigateGallery}
          onSetIndex={setGalleryIndex}
        />
      )}
      <div className={css.galleryHeader}>
        <h2 className={css.galleryTitle}>
          <FormattedMessage
            id="StoreProfilePage.tabs.gallery.title"
            values={{
              item: (
                <span className={css.itemCount}>
                  <FormattedMessage
                    id="StorefrontTabContent.gallery.itemCount"
                    values={{ count: (images?.length || 0) + (videos?.length || 0) }}
                    defaultMessage="{count, plural, one {# item} other {# items}}"
                  />
                </span>
              ),
            }}
            defaultMessage="Showcase Gallery"
          />
        </h2>
        {/* Filter */}
        <div className={css.filterContainer}>
          <button
            className={classNames(
              css.filterButton,
              activeFilter === 'all' ? css.filterActive : ''
            )}
            onClick={() => handleFilterChange('all')}
            type="button"
          >
            <FormattedMessage
              id="StorefrontTabContent.gallery.filterAll"
              defaultMessage="All"
            />
          </button>
          <button
            className={classNames(
              css.filterButton,
              activeFilter === 'photos' ? css.filterActive : ''
            )}
            onClick={() => handleFilterChange('photos')}
            type="button"
          >
            <FormattedMessage
              id="StorefrontTabContent.gallery.filterPhotos"
              defaultMessage="Photos"
            />
          </button>
          <button
            className={classNames(
              css.filterButton,
              activeFilter === 'videos' ? css.filterActive : ''
            )}
            onClick={() => handleFilterChange('videos')}
            type="button"
          >
            <FormattedMessage
              id="StorefrontTabContent.gallery.filterVideos"
              defaultMessage="Videos"
            />
          </button>
        </div>
      </div>
      {galleryGridContent}
    </div>
  );
};

/** DOM id for window scroll when store profile Reviews tab is active (see StoreProfilePage). */
export const STOREFRONT_REVIEWS_SCROLL_ANCHOR_ID = 'storefront-reviews-scroll-anchor';

const getReviewSummaryFromPublicData = publicData => {
  if (!USE_PUBLICDATA_REVIEW_SUMMARY_UI) {
    return null;
  }
  const averageRating = Number(publicData?.rating);
  const reviewCount = Number(publicData?.reviews);
  const starCounts = publicData?.starCounts;

  if (
    !Number.isFinite(averageRating) ||
    averageRating < 0 ||
    !Number.isFinite(reviewCount) ||
    reviewCount <= 0 ||
    !starCounts ||
    typeof starCounts !== 'object'
  ) {
    return null;
  }

  return {
    averageRating: Number(averageRating.toFixed(1)),
    reviewCount: Math.floor(reviewCount),
    starCounts,
  };
};

const renderReviews = (userId, achievementReviews, reviewSummary) => {
  return (
    <div
      id={STOREFRONT_REVIEWS_SCROLL_ANCHOR_ID}
      className={css.Reviewsection}
    >
      <ReviewsTab
        userId={userId}
        achievementReviews={achievementReviews}
        reviewSummary={reviewSummary}
      />
    </div>
  );
};

export const StoreHoursCard = ({ storeHours, timeZone }) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);

  const status = useMemo(() => getStoreHoursStatus(storeHours, undefined, timeZone), [
    storeHours,
    timeZone,
  ]);

  if (!storeHours) return null;

  const todayKey = status.todayKey;

  const weekdayLabel = dayKey => {
    const day = DAYS_OF_WEEK.find(d => d.key === dayKey);
    return intl.formatMessage({
      id: `StorefrontTabContent.storeHours.weekday.${dayKey}`,
      defaultMessage: day?.label || dayKey,
    });
  };

  const todayHoursText = status.todayOpenSlots
    .map(
      slot =>
        `${formatTime24WithIntl(intl, slot.openTime)} - ${formatTime24WithIntl(
          intl,
          slot.closeTime
        )}`
    )
    .join(', ');

  const summaryMessage = status.isOpen ? (
    <FormattedMessage
      id="StorefrontTabContent.storeHours.status.todaySummary"
      defaultMessage="{weekday}, {hours}"
      values={{ weekday: weekdayLabel(todayKey), hours: todayHoursText }}
    />
  ) : status.nextOpenTime && status.nextOpenIsToday ? (
    <FormattedMessage
      id="StorefrontTabContent.storeHours.status.opensToday"
      defaultMessage="Opens today at {time}"
      values={{ time: formatTime24WithIntl(intl, status.nextOpenTime) }}
    />
  ) : status.nextOpenTime ? (
    <FormattedMessage
      id="StorefrontTabContent.storeHours.status.opensOnDay"
      defaultMessage="Opens {weekday} at {time}"
      values={{
        weekday: weekdayLabel(status.nextOpenDayKey),
        time: formatTime24WithIntl(intl, status.nextOpenTime),
      }}
    />
  ) : null;

  return (
    <div
      className={classNames(css.storeHoursCard, {
        [css.storeHoursCardCollapsed]: !isExpanded,
      })}
    >
      <button
        type="button"
        className={css.storeHoursHeader}
        onClick={() => setIsExpanded(prev => !prev)}
        aria-expanded={isExpanded}
        aria-controls="store-hours-list"
      >
        <div className={css.storeHoursHeaderContent}>
          <h3 className={css.storeHoursTitle}>
            <FormattedMessage
              id="StorefrontTabContent.storeHours.title"
              defaultMessage="Store Hours"
            />
          </h3>
          <p className={css.storeHoursStatusRow}>
            <span className={css.storeHoursStatusBadge}>
              <span
                className={classNames(css.storeHoursStatusDot, {
                  [css.storeHoursStatusOpen]: status.isOpen,
                  [css.storeHoursStatusClosed]: !status.isOpen,
                })}
                aria-hidden="true"
              />
              <span
                className={classNames({
                  [css.storeHoursStatusOpen]: status.isOpen,
                  [css.storeHoursStatusClosed]: !status.isOpen,
                })}
              >
                {status.isOpen ? (
                  <FormattedMessage
                    id="StorefrontTabContent.storeHours.status.open"
                    defaultMessage="Open"
                  />
                ) : (
                  <FormattedMessage
                    id="StorefrontTabContent.storeHours.status.closed"
                    defaultMessage="Closed"
                  />
                )}
              </span>
            </span>
            {summaryMessage ? <> &middot; {summaryMessage}</> : null}
          </p>
          <p className={css.storeHoursSecondarySubtitle}>
            <FormattedMessage
              id="StorefrontTabContent.storeHours.onlineBooking"
              defaultMessage="Online booking available 24/7"
            />
          </p>
        </div>
        <span
          className={classNames(css.storeHoursChevron, {
            [css.storeHoursChevronExpanded]: isExpanded,
          })}
          aria-hidden
        >
          <i className="ri-arrow-down-s-line" />
        </span>
      </button>
      <div id="store-hours-list" className={css.storeHoursList}>
        {DAYS_OF_WEEK.map(day => {
          const dayData = storeHours[day.key];
          const isEnabled = dayData?.enabled;
          const timeSlots = dayData?.timeSlots || [];
          const isToday = day.key === todayKey;

          return (
            <div key={day.key} className={css.storeHoursRow}>
              <span className={css.storeHoursDay}>
                {intl.formatMessage(
                  {
                    id: `StorefrontTabContent.storeHours.weekday.${day.key}`,
                    defaultMessage: day.label,
                  }
                )}
              </span>
              <div className={css.storeHoursTimeContainer}>
                {isEnabled && timeSlots.length > 0 ? (
                  timeSlots.map((timeSlot, index) => {
                    const displayValue =
                      timeSlot?.openTime && timeSlot?.closeTime
                        ? `${formatTime24WithIntl(
                          intl,
                          timeSlot.openTime
                        )} - ${formatTime24WithIntl(intl, timeSlot.closeTime)}`
                        : intl.formatMessage({
                          id: 'StorefrontTabContent.storeHours.closed',
                          defaultMessage: 'Closed',
                        });

                    return (
                      <span
                        key={index}
                        className={classNames(
                          css.storeHoursTime,
                          isToday ? css.storeHoursTimeTodayOpen : null
                        )}
                      >
                        {displayValue}
                      </span>
                    );
                  })
                ) : (
                  <span
                    className={classNames(
                      css.storeHoursTime,
                      isToday ? css.storeHoursTimeTodayClosed : null
                    )}
                  >
                    <FormattedMessage
                      id="StorefrontTabContent.storeHours.closed"
                      defaultMessage="Closed"
                    />
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const renderStoreHours = (storeHours, timeZone) => {
  if (!storeHours) return null;
  return <StoreHoursCard storeHours={storeHours} timeZone={timeZone} />;
};

/** Resolves playable URL from profile shapes (Cloudinary upload vs external URL-only). */
const getStoreVideoPlaybackUrl = storeVideo => {
  if (!storeVideo || typeof storeVideo !== 'object') return '';
  const raw =
    storeVideo.playback_url ||
    storeVideo.videoUrl ||
    storeVideo.secure_url ||
    '';
  return typeof raw === 'string' ? raw.trim() : '';
};

const renderVideoBlock = (
  storeVideo,
  storeVideoTitle,
  storeVideoDescription,
  intl
) => {
  const playbackUrl = getStoreVideoPlaybackUrl(storeVideo);
  if (!playbackUrl) return null;

  const iframeTitle =
    storeVideo.seoTags?.titleTag ||
    storeVideoTitle ||
    intl.formatMessage({
      id: 'StorefrontTabContent.video.iframeTitle',
      defaultMessage: 'Store video',
    });

  const isYouTubeUrl = url => {
    return /(?:youtube\.com|youtu\.be)/.test(url);
  };

  const isDirectVideoUrl = url => {
    return /\.(mp4|webm|ogg|mov|avi|wmv|m4v)(\?.*)?$/i.test(url);
  };

  const extractYouTubeVideoId = url => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  const renderYouTubeVideo = videoId => {
    const embedUrl = `https://www.youtube.com/embed/${videoId}`;
    return (
      <iframe
        className={css.videoIframe}
        src={embedUrl}
        title={iframeTitle}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  };

  const renderDirectVideo = videoUrl => {
    return (
      <video
        className={css.videoPlayer}
        controls
        preload="metadata"
        poster={storeVideo.thumbnail || undefined}
        aria-label={storeVideo.seoTags?.altText || storeVideoTitle || iframeTitle}
        title={storeVideo.seoTags?.titleTag || storeVideoTitle || ''}
      >
        <source
          src={videoUrl}
          type={`video/${videoUrl
            .split('.')
            .pop()
            .toLowerCase()}`}
        />
        <FormattedMessage
          id="StorefrontTabContent.video.unsupported"
          defaultMessage="Your browser does not support the video tag."
        />
      </video>
    );
  };

  const renderExternalVideo = videoUrl => {
    // For other platforms or unknown URLs, try to embed as iframe
    return (
      <iframe
        className={css.videoIframe}
        src={videoUrl}
        title={iframeTitle}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  };

  let videoElement;

  if (isYouTubeUrl(playbackUrl)) {
    const videoId = extractYouTubeVideoId(playbackUrl);
    if (videoId) {
      videoElement = renderYouTubeVideo(videoId);
    } else {
      videoElement = renderExternalVideo(playbackUrl);
    }
  } else if (isDirectVideoUrl(playbackUrl)) {
    videoElement = renderDirectVideo(playbackUrl);
  } else {
    // For other platforms (Vimeo, Dailymotion, etc.) or unknown URLs
    videoElement = renderExternalVideo(playbackUrl);
  }

  return (
    <div className={css.storeIntoWithVideo}>
      <div className={css.videoCard}>
        <div className={css.videoWrapper}>{videoElement}</div>
        <div className={css.videoHeader}>
          {storeVideoTitle && (
            <h3 className={css.videoTitle}>{storeVideoTitle}</h3>
          )}
          {(storeVideoDescription || storeVideo.seoTags?.caption) && (
            <p className={css.videoDescription}>
              {storeVideoDescription || storeVideo.seoTags?.caption}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const renderVideoCard = (
  storeVideo,
  storeVideoTitle,
  storeVideoDescription,
  storeHours,
  intl,
  timeZone
) => {
  const hasVideo = !!getStoreVideoPlaybackUrl(storeVideo);
  const hasStoreHours = hasStoreHoursData(storeHours);
  if (!hasVideo && !hasStoreHours) return null;

  return (
    <div className={css.aboutSidebar}>
      {renderVideoBlock(storeVideo, storeVideoTitle, storeVideoDescription, intl)}
      {renderStoreHours(storeHours, timeZone)}
    </div>
  );
};

// Normalize blog data from various API shapes (uses `translations[locale]` when present)
const normalizeBlog = (blog, locale = 'en') => {
  const loc = locale || 'en';
  const tr = blog?.translations?.[loc] || blog?.translations?.en || {};
  const baseContent =
    typeof blog?.content === 'object' && blog?.content ? blog.content : {};
  const trContent =
    typeof tr.content === 'object' && tr.content ? tr.content : {};
  const mergedContent = { ...baseContent, ...trContent };

  const id = blog?._id || blog?.id;
  const image = blog?.image || {};
  return {
    id,
    title: tr.title || blog?.title || '',
    label: tr.label || blog?.label || '',
    excerpt:
      mergedContent.excerpt ||
      blog?.excerpt ||
      (typeof blog?.content === 'string' ? String(blog.content).slice(0, 120) : ''),
    imageUrl: image.secure_url || image.imageUrl || blog?.imageUrl || '',
    createdAt: blog?.createdAt || blog?.updatedAt || blog?.publishDate,
    metaTags: mergedContent.metaTags || blog?.metaTags || [],
    status: blog?.status,
  };
};

const BlogCard = ({ blog, userIdStr, locale = 'en' }) => {
  const intl = useIntl();
  const tagsContainerRef = useRef(null);
  useDragScroll(tagsContainerRef, css);

  const normalized = normalizeBlog(blog, locale);
  const displayDate = normalized.createdAt
    ? intl.formatDate(new Date(normalized.createdAt), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    : '';

  const listingId = blog?.listingId;
  const isListingBlog = !!listingId;
  const blogLinkParams = isListingBlog
    ? { blogId: getStoreBlogId(blog), listingId }
    : null;
  const storeLinkTarget =
    !isListingBlog && userIdStr ? getStoreBlogLinkTarget(blog, userIdStr) : null;

  const cardContent = (
    <>
      <div className={css.blogCardImageWrapper}>
        {normalized.imageUrl ? (
          <img
            src={normalized.imageUrl}
            alt={normalized.title}
            className={css.blogCardImage}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className={css.blogCardImagePlaceholder} />
        )}
      </div>
      <div className={css.blogCardContent}>
        {normalized.label && (
          <span className={css.blogCardCategory}>{normalized.label}</span>
        )}
        <h3 className={css.blogCardTitle}>{normalized.title}</h3>
        {normalized.excerpt && (
          <p className={css.blogCardDescription}>{normalized.excerpt}</p>
        )}
        {displayDate && (
          <div className={css.blogCardDate}>
            <i className="ri-calendar-line" aria-hidden />
            <span>{displayDate}</span>
          </div>
        )}
        {normalized.metaTags && normalized.metaTags.length > 0 && (
          <div
            className={css.blogCardTags}
            ref={tagsContainerRef}
            onClick={e => {
              if (tagsContainerRef.current?._dragScrollHasScrolled?.()) {
                e.stopPropagation();
                e.preventDefault();
              }
            }}
          >
            {normalized.metaTags.slice(0, 4).map(tag => (
              <span key={tag} className={css.blogCardTag}>
                <i className="ri-price-tag-3-line" aria-hidden />
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </>
  );

  const canLinkListing = isListingBlog && !!(normalized.id && listingId);
  const canLinkStore = !isListingBlog && !!storeLinkTarget;

  return (
    <article className={css.blogCard}>
      {canLinkListing ? (
        <NamedLink
          name="BlogContentPage"
          params={blogLinkParams}
          className={css.blogCardLink}
        >
          {cardContent}
        </NamedLink>
      ) : canLinkStore ? (
        <NamedLink
          name={storeLinkTarget.routeName}
          params={storeLinkTarget.params}
          className={css.blogCardLink}
        >
          {cardContent}
        </NamedLink>
      ) : (
        <div className={css.blogCardStatic}>{cardContent}</div>
      )}
    </article>
  );
};

BlogCard.propTypes = {
  locale: PropTypes.string,
  blog: PropTypes.shape({
    _id: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.string,
    label: PropTypes.string,
    excerpt: PropTypes.string,
    content: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        excerpt: PropTypes.string,
        metaTags: PropTypes.array,
      }),
    ]),
    image: PropTypes.shape({
      secure_url: PropTypes.string,
      imageUrl: PropTypes.string,
    }),
    imageUrl: PropTypes.string,
    metaTags: PropTypes.array,
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    updatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    publishDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    status: PropTypes.string,
    listingId: PropTypes.string,
    showInStore: PropTypes.bool,
    storeOrder: PropTypes.number,
  }).isRequired,
  userIdStr: PropTypes.string,
  storeName: PropTypes.string,
};

const sortListingBlogsByStoreOrder = blogs => {
  return [...(blogs || [])].sort((a, b) => {
    const aOrder = Number.isFinite(a?.storeOrder)
      ? a.storeOrder
      : Number.MAX_SAFE_INTEGER;
    const bOrder = Number.isFinite(b?.storeOrder)
      ? b.storeOrder
      : Number.MAX_SAFE_INTEGER;

    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    return (
      new Date(b?.createdAt || 0).getTime() -
      new Date(a?.createdAt || 0).getTime()
    );
  });
};

const sortStoreBlogsByStoreOrder = blogs => {
  return [...(blogs || [])].sort((a, b) => {
    const aOrder = Number.isFinite(a?.storeOrder)
      ? a.storeOrder
      : Number.MAX_SAFE_INTEGER;
    const bOrder = Number.isFinite(b?.storeOrder)
      ? b.storeOrder
      : Number.MAX_SAFE_INTEGER;

    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    return (
      new Date(b?.createdAt || 0).getTime() -
      new Date(a?.createdAt || 0).getTime()
    );
  });
};

const dedupeBlogsByKey = blogs => {
  const seen = new Map();
  (blogs || []).forEach(blog => {
    const id = blog?._id || blog?.id;
    if (!id) return;
    const listingId = blog?.listingId || '';
    const key = `${id}:${listingId}`;
    if (!seen.has(key)) {
      seen.set(key, blog);
    }
  });
  return Array.from(seen.values());
};

const renderBlogs = (
  storeName,
  storeBlogs = [],
  userId,
  {
    isCurrentUser = false,
    storeBlogsFetchCompleted = false,
    locale = 'en',
    intl,
  } = {}
) => {
  const userIdStr = userId?.uuid ?? (typeof userId === 'string' ? userId : '');
  const hasRealData = Array.isArray(storeBlogs) && storeBlogs.length > 0;
  const publishedBlogs = hasRealData
    ? dedupeBlogsByKey(storeBlogs).filter(
      b => !b.status || b.status === 'published'
    )
    : [];
  const listingBlogs = sortListingBlogsByStoreOrder(
    publishedBlogs.filter(blog => !!blog?.showInStore)
  );
  const publishedStoreBlogs = sortStoreBlogsByStoreOrder(
    publishedBlogs.filter(blog => !blog?.showInStore)
  );
  const displayCount = publishedStoreBlogs.length;
  const listingBlogsCount = listingBlogs.length;
  const hasAnyPublishedContent =
    publishedStoreBlogs.length > 0 || listingBlogsCount > 0;
  // Use empty userIdStr for dummy data so cards don't link to non-existent blog pages
  const linkUserIdStr = hasRealData ? userIdStr : '';

  if (isCurrentUser && !storeBlogsFetchCompleted) {
    return (
      <div className={css.section}>
        <BlogListSkeleton count={3} />
      </div>
    );
  }

  if (isCurrentUser && !hasAnyPublishedContent) {
    return (
      <div className={css.section}>
        <OwnerBlogTabEmptyState />
      </div>
    );
  }

  const badge = (
    <span className={css.itemCount}>
      <FormattedMessage
        id="StoreProfilePage.tabs.blog.badgeCount"
        values={{ count: displayCount }}
        defaultMessage="{count} Blogs"
      />
    </span>
  );

  const displayStoreName =
    storeName ||
    intl.formatMessage({
      id: 'StoreProfilePage.fallbackStoreName',
      defaultMessage: 'Store',
    });

  return (
    <>
      <section
        className={css.section}
        aria-labelledby="store-blogs-heading"
      >
        <header className={css.sectionHeader}>
          <h2 id="store-blogs-heading" className={css.galleryTitle}>
            <FormattedMessage
              id="StoreProfilePage.tabs.blog.title"
              values={{
                storeName: displayStoreName,
                badge,
              }}
              defaultMessage="Blogs from {storeName} {badge}"
            />
          </h2>
          <p className={css.blogSubtitle}>
            <FormattedMessage
              id="StoreProfilePage.tabs.blog.subtitle"
              defaultMessage="Helpful tips, guides, and local insights from this store to help you plan your rental with confidence."
            />
          </p>
        </header>
        <div className={css.blogCards}>
          {publishedStoreBlogs.length > 0 ? (
            publishedStoreBlogs.map(blog => (
              <BlogCard
                key={blog._id || blog.id}
                blog={blog}
                userIdStr={linkUserIdStr}
                storeName={displayStoreName}
                locale={locale}
              />
            ))
          ): (
            <div className={css.note}>
              <FormattedMessage
                id="StoreProfilePage.tabs.blog.placeholder"
                defaultMessage="Blog content will be displayed here."
              />
            </div>
          )}
        </div>
      </section>
      {listingBlogsCount > 0 ? (
        <section
          className={css.section}
          aria-labelledby="store-listing-blogs-heading"
        >
          <header className={css.sectionHeader}>
            <h3
              id="store-listing-blogs-heading"
              className={css.blogSubSectionTitle}
            >
              <FormattedMessage
                id="StoreProfilePage.tabs.blog.listingBlogs.title"
                values={{
                  badge: (
                    <span className={css.itemCount}>
                      <FormattedMessage
                        id="StoreProfilePage.tabs.blog.badgeCount"
                        values={{ count: listingBlogsCount }}
                        defaultMessage="{count} Blogs"
                      />
                    </span>
                  ),
                }}
                defaultMessage="From Our Rental Listings {badge}"
              />
            </h3>
            <p className={css.blogSubtitle}>
              <FormattedMessage
                id="StoreProfilePage.tabs.blog.listingBlogs.subtitle"
                defaultMessage="Helpful reads related to specific items you can rent from this store."
              />
            </p>
          </header>
          <div className={css.blogCards}>
            {listingBlogs.map(blog => (
              <BlogCard
                key={blog._id || blog.id}
                blog={blog}
                userIdStr={linkUserIdStr}
                storeName={displayStoreName}
                locale={locale}
              />
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
};

const renderAbout = (
  storeAbout,
  storeName,
  storeVideo,
  storeVideoTitle,
  storeVideoDescription,
  storeHours,
  isCurrentUser,
  intl,
  timeZone
) => {
  const hasVideo = !!getStoreVideoPlaybackUrl(storeVideo);
  const hasHours = hasStoreHoursData(storeHours);
  const hasAbout = isNonEmptyString(storeAbout);

  const sidebarContent =
    isCurrentUser ? (
      <div className={css.aboutSidebar}>
        {hasVideo
          ? renderVideoBlock(storeVideo, storeVideoTitle, storeVideoDescription, intl)
          : (
            <OwnerAboutEmptyWelcomeVideoCard className={css.ownerEmptyCard} />
          )}
        {hasHours
          ? renderStoreHours(storeHours, timeZone)
          : (
            <OwnerAboutEmptyStoreHoursCard className={css.ownerEmptyCard} />
          )}
      </div>
    ) : (
      renderVideoCard(
        storeVideo,
        storeVideoTitle,
        storeVideoDescription,
        storeHours,
        intl,
        timeZone
      )
    );

  return (
    <div className={css.Aboutsection}>
      {sidebarContent}


      {hasAbout ? (
        <div className={css.aboutMainSection}>
          <h2 className={css.aboutTitle}>
            <FormattedMessage
              id="StoreProfilePage.tabs.about.title"
              defaultMessage="About"
            />
            &nbsp;{storeName}
          </h2>
          <div>
            {isHTMLString(storeAbout) ? (
              <div
                className={css.content}
                dangerouslySetInnerHTML={{ __html: storeAbout }}
              />
            ) : (
              <p className={css.content}>{storeAbout}</p>
            )}
          </div>
        </div>
      ) : isCurrentUser ? (
        <OwnerAboutEmptyIntroductionCard
          storeName={storeName}
        />
      ) : (
        <div className={css.note}>
          <FormattedMessage
            id="StoreProfilePage.tabs.about.empty"
            defaultMessage="No about content yet."
          />
        </div>
      )}

    </div>
  );
};

const StorefrontTabContent = props => {
  const {
    tab,
    publicData = {},
    reviews = [],
    totalReviews = 0,
    intl,
    locale = 'en',
    userId,
    achievementReviews,
    storeBlogs = [],
    isCurrentUser,
    onReorderStoreMedia,
    storeBlogsFetchCompleted = false,
  } = props;

  const {
    storeAbout,
    storeMedia,
    storeFaqs,
    storeName,
    storeVideo,
    storeVideoTitle,
    storeVideoDescription,
    storeHours,
    storeAddress,
    origin: storeOrigin,
  } = publicData;
  const reviewSummary = getReviewSummaryFromPublicData(publicData);
  // Store hours are wall-clock times at the store's own location, not the viewer's or
  // server's — resolve the store's IANA timezone so open/closed status is computed there.
  const storeTimeZone = getStoreTimeZone(storeAddress?.origin || storeOrigin);

  const wrapClass = classNames(css.wrap);

  if (tab === 'about')
    return (
      <div className={wrapClass}>
        {renderAbout(
          storeAbout,
          storeName,
          storeVideo,
          storeVideoTitle,
          storeVideoDescription,
          storeHours,
          isCurrentUser,
          intl,
          storeTimeZone
        )}
      </div>
    );
  if (tab === 'reviews')
    return (
      <div className={wrapClass}>
        {renderReviews(userId, achievementReviews, reviewSummary)}
      </div>
    );
  if (tab === 'gallery')
    return (
      <div className={wrapClass}>
        {renderGallery(storeMedia, isCurrentUser, onReorderStoreMedia, intl, storeName)}
      </div>
    );
  if (tab === 'faqs')
    return (
      <div className={wrapClass}>{renderFaqs(storeFaqs, isCurrentUser)}</div>
    );
  if (tab === 'blog')
    return (
      <div className={wrapClass}>
        {renderBlogs(storeName, storeBlogs, userId, {
          isCurrentUser,
          storeBlogsFetchCompleted,
          locale,
          intl,
        })}
      </div>
    );
  // console.log(galleryViewIndex,"galleryViewIndex")
  // fallback
  return (
    <div className={wrapClass}>
      <div className={css.section}>
        <h2 className={css.h2}>
          <FormattedMessage
            id="StoreProfilePage.tabs.notFound"
            defaultMessage="Section not available"
          />
        </h2>
      </div>
    </div>
  );
};

StorefrontTabContent.propTypes = {
  tab: PropTypes.oneOf(['home', 'about', 'reviews', 'gallery', 'blog', 'faqs'])
    .isRequired,
  locale: PropTypes.string,
  publicData: PropTypes.shape({
    storeAbout: PropTypes.string,
    storeName: PropTypes.string,
    storeVideo: PropTypes.shape({
      videoUrl: PropTypes.string,
      thumbnail: PropTypes.string,
      seoTags: PropTypes.shape({
        altText: PropTypes.string,
        titleTag: PropTypes.string,
        caption: PropTypes.string,
      }),
    }),
    storeVideoTitle: PropTypes.string,
    storeVideoDescription: PropTypes.string,
    storeHours: PropTypes.object,
    storeMedia: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        public_id: PropTypes.string,
        secure_url: PropTypes.string,
        type: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
        seoTags: PropTypes.shape({
          altText: PropTypes.string,
          titleTag: PropTypes.string,
          caption: PropTypes.string,
          fileName: PropTypes.string,
        }),
      })
    ),
    storeFaqs: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          question: PropTypes.string,
          answer: PropTypes.string,
          q: PropTypes.string,
          a: PropTypes.string,
        }),
      ])
    ),
    storeBlogs: PropTypes.array,
  }),
  reviews: PropTypes.arrayOf(propTypes.review),
  totalReviews: PropTypes.number,
  userId: propTypes.uuid,
  achievementReviews: PropTypes.arrayOf(propTypes.review),
  isCurrentUser: PropTypes.bool,
  onReorderStoreMedia: PropTypes.func,
  storeBlogs: PropTypes.array,
  storeBlogsFetchCompleted: PropTypes.bool,
  intl: intlShape.isRequired,
};

export default injectIntl(StorefrontTabContent);
